import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

interface HelpProps {
  onClose: () => void
  theme?: 'light' | 'dark'
  onOpenGuide?: () => void
}

type HelpView = 'main' | 'help-center' | 'report-issue' | 'contact-support'

function Help({ onClose, theme = 'light', onOpenGuide }: HelpProps) {
  const [currentView, setCurrentView] = useState<HelpView>('main')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [supportMessage, setSupportMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmittingSupport, setIsSubmittingSupport] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [supportError, setSupportError] = useState<string | null>(null)
  const [supportSuccess, setSupportSuccess] = useState(false)

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (currentView !== 'main') {
          setCurrentView('main')
        } else {
          onClose()
        }
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose, currentView])

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  // Reset form when view changes
  useEffect(() => {
    if (currentView !== 'report-issue' && currentView !== 'contact-support') {
      setSubject('')
      setDescription('')
      setSupportMessage('')
      setSubmitError(null)
      setSubmitSuccess(false)
      setSupportError(null)
      setSupportSuccess(false)
    } else if (currentView === 'report-issue') {
      setSupportMessage('')
      setSupportError(null)
      setSupportSuccess(false)
    } else if (currentView === 'contact-support') {
      setSubject('')
      setDescription('')
      setSubmitError(null)
      setSubmitSuccess(false)
    }
  }, [currentView])

  // Handle bug report submission
  const handleSubmitReport = async () => {
    if (!subject.trim() || !description.trim()) {
      setSubmitError('Veuillez remplir tous les champs')
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session?.user) {
        setSubmitError('Vous devez être connecté pour envoyer un rapport')
        setIsSubmitting(false)
        return
      }

      // Insert bug report into Supabase
      const { error } = await supabase
        .from('bug_reports')
        .insert({
          user_id: session.user.id,
          subject: subject.trim(),
          description: description.trim(),
          status: 'pending'
        })

      if (error) {
        console.error('Error submitting bug report:', error)
        setSubmitError('Erreur lors de l\'envoi du rapport. Veuillez réessayer.')
        setIsSubmitting(false)
        return
      }

      // Success
      setSubmitSuccess(true)
      setSubject('')
      setDescription('')
      
      // Reset success message after 3 seconds and close modal
      setTimeout(() => {
        setSubmitSuccess(false)
        setCurrentView('main')
      }, 3000)
    } catch (error: any) {
      console.error('Error submitting bug report:', error)
      setSubmitError('Erreur lors de l\'envoi du rapport. Veuillez réessayer.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle support message submission
  const handleSubmitSupportMessage = async () => {
    if (!supportMessage.trim()) {
      setSupportError('Veuillez saisir votre message')
      return
    }

    setIsSubmittingSupport(true)
    setSupportError(null)

    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session?.user) {
        setSupportError('Vous devez être connecté pour envoyer un message')
        setIsSubmittingSupport(false)
        return
      }

      // Insert support message into bug_reports table (using same table)
      const { error } = await supabase
        .from('bug_reports')
        .insert({
          user_id: session.user.id,
          subject: 'Message de support',
          description: supportMessage.trim(),
          status: 'pending'
        })

      if (error) {
        console.error('Error submitting support message:', error)
        setSupportError('Erreur lors de l\'envoi du message. Veuillez réessayer.')
        setIsSubmittingSupport(false)
        return
      }

      // Success
      setSupportSuccess(true)
      setSupportMessage('')
      
      // Reset success message after 3 seconds and close modal
      setTimeout(() => {
        setSupportSuccess(false)
        setCurrentView('main')
      }, 3000)
    } catch (error: any) {
      console.error('Error submitting support message:', error)
      setSupportError('Erreur lors de l\'envoi du message. Veuillez réessayer.')
    } finally {
      setIsSubmittingSupport(false)
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
        onClick={onClose}
      >
        {/* Modal Container */}
        <div 
          className={`rounded-2xl shadow-2xl w-full max-w-md flex flex-col select-none transition-colors duration-200 ${
            theme === 'dark' ? 'bg-[#1A1A1A]' : 'bg-white'
          }`}
          onClick={(e) => e.stopPropagation()}
          style={{ borderRadius: '16px' }}
        >
          {/* Header */}
          <div className={`relative px-6 py-5 border-b transition-colors duration-200 ${
            theme === 'dark' ? 'border-[#2C2C2C]' : 'border-gray-100'
          }`}>
            {/* Back Button - Only show when not on main view */}
            {currentView !== 'main' && (
              <button
                onClick={() => setCurrentView('main')}
                className={`flex items-center gap-2 mb-3 transition-colors duration-150 ${
                  theme === 'dark'
                    ? 'text-[#B3B3B3] hover:text-[#FFFFFF]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">Retour</span>
              </button>
            )}
            
            <h2 className={`text-lg font-semibold transition-colors duration-200 ${
              theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
            }`}>
              {currentView === 'main' && 'Aide et support'}
              {currentView === 'help-center' && "Centre d'aide"}
              {currentView === 'report-issue' && 'Signaler un problème'}
              {currentView === 'contact-support' && 'Contacter le support'}
            </h2>
            <p className={`text-sm mt-1 transition-colors duration-200 ${
              theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-500'
            }`}>
              {currentView === 'main' && 'Comment pouvons-nous vous aider ?'}
              {currentView === 'help-center' && 'Consultez nos guides et ressources'}
              {currentView === 'report-issue' && 'Décrivez le problème rencontré'}
              {currentView === 'contact-support' && 'Notre équipe est là pour vous aider'}
            </p>
            
            {/* Close Button */}
            <button
              onClick={onClose}
              className={`absolute top-5 right-5 w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200 ${
                theme === 'dark'
                  ? 'hover:bg-[#252525] text-[#808080] hover:text-[#FFFFFF]'
                  : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
              }`}
              title="Fermer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Main Menu View */}
            {currentView === 'main' && (
              <div className="space-y-2">
                {/* Centre d'aide */}
                <button 
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (onOpenGuide) {
                      onOpenGuide()
                      onClose()
                    }
                  }}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-200 group ${
                    theme === 'dark'
                      ? 'bg-[#1A1A1A] border border-[#2C2C2C] hover:border-[#34B27B] hover:bg-[#34B27B]/10'
                      : 'bg-white border border-gray-200 hover:border-[#3ECF8E] hover:bg-[#3ECF8E]/10'
                  }`}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200" style={{ backgroundColor: '#3ECF8E' + '20' }}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#3ECF8E' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className={`text-sm font-medium transition-colors group-hover:text-[#3ECF8E] ${
                      theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                    }`}>Centre d'aide</p>
                    <p className={`text-xs mt-0.5 transition-colors ${
                      theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-500'
                    }`}>Parcourir les guides et tutoriels</p>
                  </div>
                  <svg className={`w-5 h-5 transition-colors duration-200 group-hover:text-[#3ECF8E] ${
                    theme === 'dark' ? 'text-[#808080]' : 'text-gray-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Signaler un problème */}
                <button 
                  onClick={() => setCurrentView('report-issue')}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-200 group ${
                    theme === 'dark'
                      ? 'bg-[#1A1A1A] border border-[#2C2C2C] hover:border-[#34B27B] hover:bg-[#34B27B]/10'
                      : 'bg-white border border-gray-200 hover:border-[#3ECF8E] hover:bg-[#3ECF8E]/10'
                  }`}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200" style={{ backgroundColor: '#3ECF8E' + '20' }}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#3ECF8E' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className={`text-sm font-medium transition-colors group-hover:text-[#3ECF8E] ${
                      theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                    }`}>Signaler un problème</p>
                    <p className={`text-xs mt-0.5 transition-colors ${
                      theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-500'
                    }`}>Rapporter un bug ou une erreur</p>
                  </div>
                  <svg className={`w-5 h-5 transition-colors duration-200 group-hover:text-[#3ECF8E] ${
                    theme === 'dark' ? 'text-[#808080]' : 'text-gray-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Contacter le support */}
                <button 
                  onClick={() => setCurrentView('contact-support')}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-200 group ${
                    theme === 'dark'
                      ? 'bg-[#1A1A1A] border border-[#2C2C2C] hover:border-[#34B27B] hover:bg-[#34B27B]/10'
                      : 'bg-white border border-gray-200 hover:border-[#3ECF8E] hover:bg-[#3ECF8E]/10'
                  }`}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200" style={{ backgroundColor: '#3ECF8E' + '20' }}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#3ECF8E' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className={`text-sm font-medium transition-colors group-hover:text-[#3ECF8E] ${
                      theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                    }`}>Contacter le support</p>
                    <p className={`text-xs mt-0.5 transition-colors ${
                      theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-500'
                    }`}>Parler avec notre équipe</p>
                  </div>
                  <svg className={`w-5 h-5 transition-colors duration-200 group-hover:text-[#3ECF8E] ${
                    theme === 'dark' ? 'text-[#808080]' : 'text-gray-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}

            {/* Help Center View */}
            {currentView === 'help-center' && (
              <div className="space-y-4">
                <div className="space-y-3">
                  <button className="w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors duration-150">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">🚀 Guide de démarrage</h3>
                    <p className="text-xs text-gray-500">Apprenez les bases d'Avocat AI</p>
                  </button>
                  
                  <button className="w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors duration-150">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">📝 Rédaction de documents</h3>
                    <p className="text-xs text-gray-500">Utilisez l'éditeur IA efficacement</p>
                  </button>
                  
                  <button className="w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors duration-150">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">💾 Gestion du Vault</h3>
                    <p className="text-xs text-gray-500">Organisez vos documents juridiques</p>
                  </button>
                  
                  <button className="w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors duration-150">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">❓ FAQ</h3>
                    <p className="text-xs text-gray-500">Questions fréquemment posées</p>
                  </button>
                </div>
              </div>
            )}

            {/* Report Issue View */}
            {currentView === 'report-issue' && (
              <div className="space-y-4">
                {submitSuccess ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#3ECF8E' + '20' }}>
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#3ECF8E' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">Rapport envoyé avec succès !</h3>
                    <p className="text-sm text-gray-600">
                      Merci pour votre retour. Nous examinerons votre rapport sous peu.
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sujet</label>
                      <input
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Ex: Erreur lors de l'export PDF"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                        style={{ '--tw-ring-color': '#3ECF8E' } as React.CSSProperties & { '--tw-ring-color': string }}
                        disabled={isSubmitting}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description du problème</label>
                      <textarea
                        rows={6}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Décrivez le problème rencontré en détail..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm resize-none"
                        style={{ '--tw-ring-color': '#3ECF8E' } as React.CSSProperties & { '--tw-ring-color': string }}
                        disabled={isSubmitting}
                      />
                    </div>

                    {submitError && (
                      <div className="p-3 rounded-lg" style={{ backgroundColor: '#fee2e2', borderColor: '#fca5a5', borderWidth: '1px', borderStyle: 'solid' }}>
                        <p className="text-sm" style={{ color: '#dc2626' }}>{submitError}</p>
                      </div>
                    )}
                    
                    <button 
                      onClick={handleSubmitReport}
                      disabled={isSubmitting || !subject.trim() || !description.trim()}
                      className="w-full px-4 py-3 text-white font-medium rounded-lg transition-colors duration-150 text-sm disabled:opacity-50 disabled:cursor-not-allowed" 
                      style={{ 
                        backgroundColor: isSubmitting || !subject.trim() || !description.trim() ? '#9ca3af' : '#3ECF8E'
                      }} 
                      onMouseEnter={(e) => {
                        if (!isSubmitting && subject.trim() && description.trim()) {
                          e.currentTarget.style.backgroundColor = '#25A26D'
                        }
                      }} 
                      onMouseLeave={(e) => {
                        if (!isSubmitting && subject.trim() && description.trim()) {
                          e.currentTarget.style.backgroundColor = '#3ECF8E'
                        }
                      }}
                    >
                      {isSubmitting ? 'Envoi en cours...' : 'Envoyer le rapport'}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Contact Support View */}
            {currentView === 'contact-support' && (
              <div className="space-y-4">
                <div className="rounded-lg p-4" style={{ backgroundColor: '#3ECF8E' + '15', borderColor: '#3ECF8E' + '40', borderWidth: '1px', borderStyle: 'solid' }}>
                  <h3 className="text-sm font-semibold mb-2" style={{ color: '#3ECF8E' }}>Contactez notre équipe</h3>
                  <p className="text-sm mb-3" style={{ color: '#3ECF8E' }}>
                    Notre équipe de support est disponible pour vous aider
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2" style={{ color: '#3ECF8E' }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>support@avocatai.com</span>
                    </div>
                    <div className="flex items-center gap-2" style={{ color: '#3ECF8E' }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Lun - Ven: 9h - 18h</span>
                    </div>
                  </div>
                </div>

                {supportSuccess ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#3ECF8E' + '20' }}>
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#3ECF8E' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">Message envoyé avec succès !</h3>
                    <p className="text-sm text-gray-600">
                      Merci pour votre message. Notre équipe vous répondra sous peu.
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Votre message</label>
                      <textarea
                        rows={5}
                        value={supportMessage}
                        onChange={(e) => setSupportMessage(e.target.value)}
                        placeholder="Décrivez votre demande..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm resize-none"
                        style={{ '--tw-ring-color': '#3ECF8E' } as React.CSSProperties & { '--tw-ring-color': string }}
                        disabled={isSubmittingSupport}
                      />
                    </div>

                    {supportError && (
                      <div className="p-3 rounded-lg" style={{ backgroundColor: '#fee2e2', borderColor: '#fca5a5', borderWidth: '1px', borderStyle: 'solid' }}>
                        <p className="text-sm" style={{ color: '#dc2626' }}>{supportError}</p>
                      </div>
                    )}
                    
                    <button 
                      onClick={handleSubmitSupportMessage}
                      disabled={isSubmittingSupport || !supportMessage.trim()}
                      className="w-full px-4 py-3 text-white font-medium rounded-lg transition-colors duration-150 text-sm disabled:opacity-50 disabled:cursor-not-allowed" 
                      style={{ 
                        backgroundColor: isSubmittingSupport || !supportMessage.trim() ? '#9ca3af' : '#3ECF8E'
                      }} 
                      onMouseEnter={(e) => {
                        if (!isSubmittingSupport && supportMessage.trim()) {
                          e.currentTarget.style.backgroundColor = '#25A26D'
                        }
                      }} 
                      onMouseLeave={(e) => {
                        if (!isSubmittingSupport && supportMessage.trim()) {
                          e.currentTarget.style.backgroundColor = '#3ECF8E'
                        }
                      }}
                    >
                      {isSubmittingSupport ? 'Envoi en cours...' : 'Envoyer le message'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default Help
